import { Injectable, signal } from '@angular/core';
import { CalClient, Config, ICvxClaimsPrincipal } from '@cvx/cal';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class CalAuthService {
  readonly user = signal<ICvxClaimsPrincipal | null>(null);

  /**
   * True when the signed-in user belongs to the configured admin AAD group.
   * Stays false until the group check resolves or when no adminGroupId is set.
   */
  readonly isAdmin = signal(false);

  private readonly client: CalClient;

  constructor() {
    console.log('[CalAuth] Service initializing...');

    const config: Config = {
      autoSignIn: false,
      popupForLogin: false,
      cacheLocation: 'localStorage',
      instance: 'https://login.microsoftonline.com/',
      tenantId: environment.calTenantId,
      clientId: environment.calClientId,
      redirectUri: environment.calRedirectUri,
      oidcScopes: ['openid', 'profile', 'email'],
      graphScopes: ['User.Read', 'GroupMember.Read.All'],
    };

    this.client = new CalClient(config);
    console.log('[CalAuth] CalClient created');

    // Bypass CalClient's internal async handling which has fire-and-forget bugs.
    // Access the underlying MSAL PublicClientApplication directly to properly
    // sequence: handleRedirectPromise → check accounts → loginRedirect.
    this.performAuth();
  }

  async signIn(): Promise<ICvxClaimsPrincipal | null> {
    try {
      const principal = await this.client.userInitiatedSignIn();
      this.user.set(principal ?? null);
      if (principal) await this.checkAdminGroup();
      return principal ?? null;
    } catch (error) {
      console.error('CalAuthService: sign-in failed', error);
      return null;
    }
  }

  async signOut(): Promise<void> {
    await this.client.userInitiatedSignOut();
    this.user.set(null);
    this.isAdmin.set(false);
  }

  /**
   * Checks whether the current user is a member of the configured admin AAD group
   * and updates the `isAdmin` signal. Safe to call multiple times.
   * 
   * First attempts to check the `groups` claim in the ID token (if available).
   * Falls back to a direct Microsoft Graph API call if needed.
   */
  private async checkAdminGroup(): Promise<void> {
    const groupId = environment.adminGroupId;
    if (!groupId) return; // adminGroupId not configured — hide danger zone for everyone
    try {

      // First, try to get groups from the ID token claim
      const idTokenClaims = this.client.getIdTokenClaims() as any;
      if (idTokenClaims?.groups && Array.isArray(idTokenClaims.groups)) {
        const isInGroup = idTokenClaims.groups.includes(groupId);
        this.isAdmin.set(isInGroup);
        return;
      }

      // No groups claim in ID token, call Graph API
      const token = await this.getToken(['GroupMember.Read.All']);
      if (!token) {
        this.isAdmin.set(false);
        return;
      }

      const graphResponse = await fetch('https://graph.microsoft.com/v1.0/me/memberOf?$top=999', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!graphResponse.ok) {
        this.isAdmin.set(false);
        return;
      }

      const graphData = await graphResponse.json() as any;
      const groupIds = graphData.value?.map((item: any) => item.id) || [];
      const isInGroup = groupIds.includes(groupId);
      this.isAdmin.set(isInGroup);
    } catch (err) {
      this.isAdmin.set(false);
    }
  }

  isSignedIn(): boolean {
    return this.user() !== null;
  }

  async getToken(scopes?: string[]): Promise<string | null> {
    try {
      const token = await this.client.getAADToken(scopes) as string | null;
      return token;
    } catch (error) {
      console.error('CalAuthService: failed to get token', error);
      return null;
    }
  }

  async getClaims(): Promise<ICvxClaimsPrincipal | null> {
    try {
      return await this.client.getClaims();
    } catch (error) {
      console.error('CalAuthService: failed to get claims', error);
      return null;
    }
  }

  private async performAuth(): Promise<void> {
    try {
      const msalApp = (this.client as any)._clientApplication;
      if (!msalApp) {
        console.error('[CalAuth] FATAL: _clientApplication is null/undefined');
        return;
      }

      // Step 1: Wait for MSAL to finish processing any pending redirect.
      console.log('[CalAuth] Awaiting handleRedirectPromise...');
      const redirectResult = await msalApp.handleRedirectPromise();
      console.log('[CalAuth] handleRedirectPromise resolved:', redirectResult ? 'got token response' : 'null (no redirect)');

      if (redirectResult !== null) {
        // We are on the AAD return page (just came back from login).
        // CalClient's internal handleResponse callback is ALREADY processing this —
        // it will populate claims, save to localStorage, then call location.reload().
        // If we also call userInitiatedSignIn() here we get a race: two concurrent
        // Graph calls, and if either fails before reload, isUserSignedIn() returns
        // false → loginRedirect() fires again → infinite loop.
        // Solution: do nothing. Let CalClient finish and reload the page.
        console.log('[CalAuth] AAD return detected — CalClient is handling reload, standing by...');
        return;
      }

      // Step 2: Normal page load. Check for a cached session.
      const accounts = msalApp.getAllAccounts();
      console.log(`[CalAuth] Found ${accounts.length} cached account(s)`);

      if (accounts.length === 0) {
        // No session at all — send the user to AAD.
        console.log('[CalAuth] No session found. Initiating AAD login redirect...');
        await msalApp.loginRedirect({
          scopes: ['openid', 'profile', 'email'],
          // On localhost the Chevron BSSO Chrome extension intercepts AAD redirects
          // and attempts seamless SSO via /reprocess, which breaks MSAL's PKCE flow.
          // 'select_account' bypasses that path. On deployed environments (chevron.com
          // domains) BSSO completes correctly without this prompt.
          ...(environment.isLocalhost ? { prompt: 'select_account' } : {})
        });
        return; // browser navigates away — nothing after this runs
      }

      // Step 3: Account exists. Restore the claims principal.
      // If this fails (e.g. Graph error) we catch it and keep the user signal null
      // rather than triggering another redirect — that would cause an infinite loop.
      console.log('[CalAuth] Session found. Restoring claims...');
      try {
        const principal = await this.client.userInitiatedSignIn();
        this.user.set(principal ?? null);
        if (principal) await this.checkAdminGroup();
        console.log('[CalAuth] User authenticated:', principal?.name);
      } catch (claimsError) {
        console.error('[CalAuth] Claims restoration failed (Graph error?):', claimsError);
        // User has a valid MSAL session — they are logged in to AAD.
        // We just couldn't get the full Chevron claims principal.
        // Do NOT redirect again; that causes the loop.
      }

    } catch (error) {
      console.error('[CalAuth] Authentication failed:', error);
    }
  }
}
